import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination, Autoplay } from "swiper/modules";
import "typeface-raleway";

const shine = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const Page3 = () => {
  const [visibleCards, setVisibleCards] = useState({});
  const cardRefs = useRef([]);
  const swiperRef = useRef(null);

  const getCardGradient = (number) => {
    const gradients = {
      "1": "linear-gradient(to right, #A47300, #FCB100)",
      "2": "linear-gradient(to right, #006B3D, #00D17B)",
      "3": "linear-gradient(to right, #8B0000, #FF4040)",
      "4": "linear-gradient(to right, #4B0082, #9370DB)",
      "5": "linear-gradient(to right, #004D40, #00BFA5)",
      "6": "linear-gradient(to right, #1A237E, #3F51B5)",
      "7": "linear-gradient(to right, #1B5E20, #4CAF50)",
      "8": "linear-gradient(to right, #0D47A1, #2196F3)",
      "9": "linear-gradient(to right, #311B92, #673AB7)",
      "10": "linear-gradient(to right, #B71C1C, #F44336)",
      "11": "linear-gradient(to right, #01579B, #03A9F4)",
      "12": "linear-gradient(to right, #FF6F00, #FFC107)",
      "13": "linear-gradient(to right, #BF360C, #FF5722)",
      "14": "linear-gradient(to right, #880E4F, #E91E63)",
      "15": "linear-gradient(to right, #4A148C, #9C27B0)",
      "16": "linear-gradient(to right, #006064, #00BCD4)",
      "17": "linear-gradient(to right, #3E2723, #795548)"
    };
    return gradients[number] || "transparent";
  };

  const getTextGradient = (number) => {
    const gradients = {
      "1": "linear-gradient(90deg, #FFFFFF, #FFE0B2)", // Gold/Amber contrast
      "2": "linear-gradient(90deg, #FFFFFF, #B2DFDB)", // Teal/Green contrast
      "3": "linear-gradient(90deg, #FFFFFF, #FFCDD2)", // Soft Red/Pink contrast
      "4": "linear-gradient(90deg, #FFFFFF, #E1BEE7)", // Lavender/Purple contrast
      "5": "linear-gradient(90deg, #FFFFFF, #B2DFDB)", // Teal/Green contrast
      "6": "linear-gradient(90deg, #FFFFFF, #C5CAE9)", // Light Blue/Indigo contrast
      "7": "linear-gradient(90deg, #FFFFFF, #C8E6C9)", // Light Green contrast
      "8": "linear-gradient(90deg, #FFFFFF, #BBDEFB)", // Light Blue contrast
      "9": "linear-gradient(90deg, #FFFFFF , #D1C4E9)", // Soft Purple contrast
      "10": "linear-gradient(90deg, #FFFFFF, #FFCDD2)", // Soft Red/Pink contrast
      "11": "linear-gradient(90deg, #FFFFFF, #B3E5FC)", // Sky Blue contrast
      "12": "linear-gradient(90deg, #FFFFFF, #FFE0B2)", // Gold/Amber contrast
      "13": "linear-gradient(90deg, #FFFFFF, #FFCCBC)", // Soft Orange contrast
      "14": "linear-gradient(90deg, #FFFFFF, #F8BBD0)", // Pink contrast
      "15": "linear-gradient(90deg, #FFFFFF, #E1BEE7)", // Lavender/Purple contrast
      "16": "linear-gradient(90deg, #FFFFFF, #B2EBF2)", // Cyan contrast
      "17": "linear-gradient(90deg, #FFFFFF, #D7CCC8)" // Brown/Taupe contrast
    };
    return gradients[number] || "transparent";
  };

  const cardLayouts = [
    { number: "1", title: "Affordable and clean energy" },
    { number: "2", title: "Industry, innovation and infrastructure" },
    { number: "3", title: "Climate Action" },
    { number: "4", title: "Sustainable cities communities" },
    { number: "5", title: "Responsible consumption and production" },
    { number: "6", title: "Decent Work And Economic Growth" },
    { number: "7", title: "Life on Land" },
    { number: "8", title: "Life Below Water" },
    { number: "9", title: "Peace, Justice And Strong Institutions" },
    { number: "10", title: "Good Health And Well-Being" },
    { number: "11", title: "Clean Water and Sanitation" },
    { number: "12", title: "Zero Hunger" },
    { number: "13", title: "No Poverty" },
    { number: "14", title: "Quality Education" },
    { number: "15", title: "Gender Equality" },
    { number: "16", title: "Reduced Inequalities" },
    { number: "17", title: "Partnerships For The Goals" }
  ];

  useEffect(() => {
    const observers = cardRefs.current.map((ref, index) => {
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          setVisibleCards(prev => ({
            ...prev,
            [index]: entry.intersectionRatio >= 0.5
          }));
        },
        {
          threshold: [0.5] // Trigger when 50% of the card is visible
        }
      );

      if (ref) {
        observer.observe(ref);
      }

      return observer;
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: "#000",
        color: "#fff",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative"
      }}
    >
      <Swiper
        ref={swiperRef}
        modules={[Pagination, Autoplay]}
        pagination={{ clickable: true }}
        autoplay={{
          delay: 3000, // 3 seconds per slide
          disableOnInteraction: false // continue autoplay after user interaction
        }}
        spaceBetween={30}
        slidesPerView={1}
        style={{
          width: "100%",
          maxWidth: "490px",
          height: "600px"
        }}
      >
        {cardLayouts.map((card, index) => (
          <SwiperSlide key={card.number}>
            <Box
              ref={el => cardRefs.current[index] = el}
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                background: getCardGradient(card.number),
                borderRadius: "16px",
                opacity: visibleCards[index] ? 1 : 0.3,
                transform: visibleCards[index] ? 'scale(1)' : 'scale(0.9)',
                transition: 'all 0.5s ease-in-out',
                backgroundSize: '200% 100%',
                position: 'relative',
                overflow: 'hidden',
                textAlign: "center"
              }}
            >
              {/* Shining effect layer */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '-50%',
                  left: '-50%',
                  width: '200%',
                  height: '200%',
                  background: 'linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.2) 35%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0.2) 65%, transparent 75%)',
                  backgroundSize: '200% 200%',
                  opacity: visibleCards[index] ? 0.3 : 0, // Increased opacity when visible
                  transition: 'opacity 0.3s ease',
                  animation: visibleCards[index] ? `${shine} 3s infinite linear` : 'none', // Slightly slower animation
                  pointerEvents: 'none',
                  zIndex: 1
                }}
              />
               {/* Gradient Overlay */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          top: '43%',
          left: 0,
          right: 0,
          height: '40vh',
          background: 'linear-gradient(to bottom, transparent, black)',
          zIndex: 1,
          pointerEvents: 'none'
        }}
        />
         <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          top: "-2%",
          left: 0,
          right: 0,
          height: '40vh',
          background: 'linear-gradient(to top, transparent, black)',
          zIndex: 1,
          pointerEvents: 'none'
        }}
        />
              <Typography
                sx={{
                  color: "transparent",
                  fontFamily: "Poppins",
                  fontSize: "68px",
                  fontWeight: "bold",
                  WebkitTextStroke: "1px #FFFFFF",
                  textStroke: "1px #CB8B67",
                  position: "relative",
                  marginBottom: "-15px",
                  zIndex: 4,
                  position: 'relative',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              >
                {card.number}
              </Typography>
              <Typography
               sx={{
                fontFamily: "Raleway",
                fontSize: "24px",
                background: getTextGradient(card.number),
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                color: "transparent",
                fontWeight: "bold",
                textAlign: "center",
                zIndex: 5,
                position: 'relative'
              }}
              >
                {card.title.toUpperCase()}
              </Typography>
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
};

export default Page3;